import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import {
  announcementCampaign,
  isAnnouncementActive,
  type AnnouncementCampaign,
} from "@/config/announcements";
import { getLocalizedPath } from "@/utils/routes";
import { trackEvent } from "@/lib/analytics";
import { AnalysisCta } from "./AnalysisCta";

// Browser-only writes; keeps dismissal through SPA remounts when storage is blocked.
const dismissedInMemory = new Set<string>();
const linkClass =
  "inline-flex min-h-11 min-w-0 items-center justify-center gap-2 rounded px-2 py-2 text-sm font-medium leading-6 text-coral-text underline decoration-transparent underline-offset-4 hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-text";

export function AnnouncementBar({
  campaign = announcementCampaign,
}: {
  campaign?: AnnouncementCampaign;
}) {
  const storageKey = `caire.announcement.dismissed.${campaign.id}`;
  const { pathname } = useLocation();
  const locale = /^\/en(?:\/|$)/.test(pathname) ? "en" : "sv";
  const copy = campaign.copy[locale];
  // Server and first client render agree. Time and session storage are read in effects.
  const [visible, setVisible] = useState(false);
  const region = useRef<HTMLElement>(null);
  const viewed = useRef(new Set<string>());
  const track = (action: "view" | "click" | "dismiss") => {
    trackEvent(`announcement_${action}`, {
      campaign_id: campaign.id,
      placement: "below_navigation",
      locale,
    });
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const update = () => {
      let dismissed = dismissedInMemory.has(campaign.id);
      try {
        dismissed ||= sessionStorage.getItem(storageKey) === "1";
      } catch {
        /* A storage restriction must not break navigation. */
      }
      const now = Date.now();
      setVisible(!dismissed && isAnnouncementActive(campaign, pathname, now));
      const boundary = [
        Date.parse(campaign.startsAt),
        Date.parse(campaign.endsAt),
      ].find((at) => at > now);
      if (boundary)
        timer = setTimeout(update, Math.min(boundary - now, 2_147_483_647));
    };
    update();
    return () => clearTimeout(timer);
  }, [pathname, campaign, storageKey]);

  useEffect(() => {
    if (!visible || !region.current || viewed.current.has(campaign.id)) return;
    const reportView = () => {
      if (viewed.current.has(campaign.id)) return;
      viewed.current.add(campaign.id);
      trackEvent("announcement_view", {
        campaign_id: campaign.id,
        placement: "below_navigation",
        locale,
      });
    };
    if (typeof IntersectionObserver === "undefined") {
      reportView();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        reportView();
        observer.disconnect();
      }
    });
    observer.observe(region.current);
    return () => observer.disconnect();
  }, [visible, locale, campaign.id]);

  const dismiss = () => {
    dismissedInMemory.add(campaign.id);
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      /* Session memory remains. */
    }
    track("dismiss");
    setVisible(false);
    document
      .getElementById("marketing-content")
      ?.focus({ preventScroll: true });
  };

  if (
    !visible ||
    !isAnnouncementActive(campaign, pathname, Date.now()) ||
    pathname === getLocalizedPath(campaign.destination, locale)
  )
    return null;
  return (
    <aside
      ref={region}
      aria-label={copy.title}
      className="border-b border-hairline bg-sand text-ink"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-1 sm:px-6">
        {campaign.destination === "freeAnalysis" ? (
          <AnalysisCta
            locale={locale}
            cta="analysis_banner"
            label={`${copy.title} · ${copy.action}`}
            className={linkClass}
            onClick={() => track("click")}
          />
        ) : (
          <Link
            to={getLocalizedPath(campaign.destination, locale)}
            className={linkClass}
            onClick={() => track("click")}
          >
            {copy.title} · {copy.action}
            <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
          </Link>
        )}
        <button
          type="button"
          aria-label={copy.dismiss}
          onClick={dismiss}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded hover:bg-sand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-text"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
